import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Box,
  Chip,
  Alert,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Divider,
  Stack } from
'@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  BugReport as TicketIcon,
  CheckCircle as ResolvedIcon,
  HourglassEmpty as PendingIcon,
  Build as InProgressIcon,
  Cancel as ClosedIcon,
  Warning as HighPriorityIcon,
  ArrowUpward as MediumPriorityIcon,
  ArrowDownward as LowPriorityIcon } from
'@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import { formatDateTime } from '../../utils/dateUtils';
import CreateForm from '../Tickets/CreateForm';
import { ticketsApi } from '../../services/apiClient';
import { useIsMobile } from '../../hooks/useResponsive';import { useThemeSettings } from "../../contexts/ThemeContext";

const TicketsTable = ({
  locationId,
  locationData, // Full location object when available
  onViewTicket,
  onEditTicket,
  onDeleteTicket,
  tickets: initialTickets = null,
  loading: initialLoading = false,
  selectedDevice // Device object when creating ticket from device page
}) => {const { t } = useThemeSettings();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const { hasPermission } = usePermissions();
  const canManageTickets = hasPermission('tickets.manage');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (initialTickets) {
      setTickets(Array.isArray(initialTickets) ? initialTickets : []);
      setLoading(false);
    } else if (locationId) {
      fetchTicketsForLocation(locationId);
    }
  }, [locationId, initialTickets]);

  useEffect(() => {
    // Filter tickets based on search term
    const filtered = tickets.filter((ticket) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        ticket.title?.toLowerCase().includes(searchLower) ||
        ticket.ticketNumber?.toLowerCase().includes(searchLower) ||
        ticket.status?.toLowerCase().includes(searchLower) ||
        ticket.priority?.toLowerCase().includes(searchLower) ||
        ticket.assignee?.toLowerCase().includes(searchLower));

    });
    setFilteredTickets(filtered);
  }, [tickets, searchTerm]);

  const fetchTicketsForLocation = async (locId) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch tickets from API
      const response = await ticketsApi.getByLocation(locId);

      let ticketsData = [];
      if (response?.data) {
        if (Array.isArray(response.data)) {
          ticketsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          ticketsData = response.data.data;
        } else if (response.data.tickets && Array.isArray(response.data.tickets)) {
          ticketsData = response.data.tickets;
        }
      }

      // Transform the data to match component expectations
      const transformedTickets = ticketsData.map((ticket) => {
        // Safely extract support user name
        let assigneeName = null;
        if (ticket.supportUser) {
          const firstName = ticket.supportUser.firstName || '';
          const lastName = ticket.supportUser.lastName || '';
          assigneeName = `${firstName} ${lastName}`.trim() || null;
        }

        // Safely extract reporter name
        let reporterName = 'Unknown';
        if (ticket.createdByUser) {
          const firstName = ticket.createdByUser.firstName || '';
          const lastName = ticket.createdByUser.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          reporterName = fullName || ticket.createdByUser.email || ticket.createdByUser.username || 'Unknown';
        }

        return {
          id: ticket.id || ticket._id,
          ticketNumber: ticket.ticketNumber || `TKT-${ticket.id || ticket._id}`,
          title: ticket.title || ticket.description?.substring(0, 50),
          description: ticket.description,
          status: ticket.status || 'open',
          priority: ticket.urgency || ticket.priority || 'medium',
          assignee: assigneeName,
          reporter: reporterName,
          createdAt: ticket.created_at || ticket.createdAt,
          updatedAt: ticket.updated_at || ticket.updatedAt,
          locationId: ticket.locationId || ticket.location_id || locId,
          urgency: ticket.urgency,
          support_type: ticket.support_type,
          steps_tried: ticket.steps_tried,
          next_step: ticket.next_step
        };
      });

      setTickets(transformedTickets);
    } catch (err) {
      setError('Failed to load tickets. Please try again.');
      console.error('Error loading tickets:', err);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (locationId) {
      fetchTicketsForLocation(locationId);
    }
  };

  const handleDelete = async (ticketId) => {
    if (!canManageTickets) return;

    try {
      // Delete ticket via API
      await ticketsApi.delete(ticketId);

      setTickets(tickets.filter((ticket) => ticket.id !== ticketId));

      if (onDeleteTicket) {
        onDeleteTicket(ticketId);
      }
    } catch (err) {
      setError('Failed to delete ticket. Please try again.');
      console.error('Error deleting ticket:', err);
    }
  };

  const handleOpenDialog = () => setCreateDialogOpen(true);
  const handleCloseDialog = () => setCreateDialogOpen(false);

  const handleTicketCreated = (newTicket) => {
    setTickets([newTicket, ...tickets]);
    handleCloseDialog();
  };

  const getStatusChip = (status) => {const { t } = useThemeSettings();
    const statusConfig = {
      'open': { label: 'Open', color: 'error', icon: <PendingIcon /> },
      'pending': { label: 'Pending', color: 'warning', icon: <PendingIcon /> },
      'in-progress': { label: 'In Progress', color: 'info', icon: <InProgressIcon /> },
      'resolved': { label: 'Resolved', color: 'success', icon: <ResolvedIcon /> },
      'closed': { label: 'Closed', color: 'default', icon: <ClosedIcon /> }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig['open'];

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined" />);


  };

  const getPriorityChip = (priority) => {const { t } = useThemeSettings();
    const priorityConfig = {
      'high': { label: 'High', color: 'error', icon: <HighPriorityIcon /> },
      'medium': { label: 'Medium', color: 'warning', icon: <MediumPriorityIcon /> },
      'low': { label: 'Low', color: 'success', icon: <LowPriorityIcon /> }
    };

    const config = priorityConfig[priority?.toLowerCase()] || priorityConfig['medium'];

    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small" />);


  };

  // Always render the Create Ticket button and dialog
  const createTicketButton =
  <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
      <Button
      variant="contained"
      color="primary"
      size="small"
      onClick={handleOpenDialog}>{t ? t("TicketsTable.create_ticket") : "Create Ticket"}


    </Button>
      {/* Search and Actions Bar */}
      <TextField
      size="small"
      placeholder={t ? t("TicketsTable.search_tickets") : "Search tickets..."}
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      InputProps={{
        startAdornment:
        <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>

      }}
      sx={{ flexGrow: 1, maxWidth: 300 }} />
    
      <Tooltip title={t ? t("DeviceStatusDashboard.refresh") : "Refresh"}>
        <IconButton onClick={handleRefresh} size="small">
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </Box>;


  // State to store fetched location data for device
  const [deviceLocation, setDeviceLocation] = useState(null);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  // Helper function to fetch full location hierarchy
  const fetchLocationHierarchy = async (locationId) => {
    try {
      // Fetch location hierarchy from API
      const response = await ticketsApi.getLocationHierarchy(locationId);

      if (response?.data) {
        return response.data;
      }

      // Fallback to basic structure if API doesn't return expected data
      return {
        id: locationId,
        name: `Location ${locationId}`,
        friendlyName: `Location ${locationId}`,
        type: 'unit',
        path: 'Unknown Path'
      };
    } catch (error) {
      console.error('Error fetching location hierarchy:', error);
      // Return basic structure on error
      return {
        id: locationId,
        name: `Location ${locationId}`,
        friendlyName: `Location ${locationId}`,
        type: 'unit',
        path: 'Unknown Path'
      };
    }
  };

  // Fetch location data when device is selected
  useEffect(() => {
    const fetchDeviceLocation = async () => {
      // Check if device has location information
      if (selectedDevice) {
        // First check if device has a locationModel (like in your example)
        if (selectedDevice.locationModel) {
          //console.log('Device has locationModel:', selectedDevice.locationModel);

          setFetchingLocation(true);
          try {
            // Fetch the complete hierarchy for this location
            const fullLocation = await fetchLocationHierarchy(selectedDevice.locationModel.id);

            // Merge the basic info with hierarchy
            const completeLocation = {
              ...selectedDevice.locationModel,
              ...fullLocation,
              friendlyName: selectedDevice.locationModel.friendlyName || fullLocation.friendlyName,
              path: fullLocation.path
            };

            setDeviceLocation(completeLocation);
            //console.log('Fetched complete location hierarchy:', completeLocation);
          } catch (error) {
            console.error('Failed to fetch location hierarchy:', error);
            // Use the locationModel as fallback
            setDeviceLocation(selectedDevice.locationModel);
          } finally {
            setFetchingLocation(false);
          }
        }
        // Check for deviceLocation field
        else if (selectedDevice.deviceLocation) {
          const locId = selectedDevice.deviceLocation;
          //console.log('Device has deviceLocation ID:', locId);

          setFetchingLocation(true);
          try {
            const fullLocation = await fetchLocationHierarchy(locId);
            setDeviceLocation(fullLocation);
            //console.log('Fetched location for deviceLocation:', fullLocation);
          } catch (error) {
            console.error('Failed to fetch location:', error);
            setDeviceLocation({
              id: locId,
              name: `Location ${locId}`,
              friendlyName: `Location ${locId}`
            });
          } finally {
            setFetchingLocation(false);
          }
        }
        // Check for locationId field
        else if (selectedDevice.locationId || selectedDevice.location_id) {
          const locId = selectedDevice.locationId || selectedDevice.location_id;
          //console.log('Device has locationId:', locId);

          setFetchingLocation(true);
          try {
            const fullLocation = await fetchLocationHierarchy(locId);
            setDeviceLocation(fullLocation);
            //console.log('Fetched location for locationId:', fullLocation);
          } catch (error) {
            console.error('Failed to fetch location:', error);
            setDeviceLocation({
              id: locId,
              name: `Location ${locId}`,
              friendlyName: `Location ${locId}`
            });
          } finally {
            setFetchingLocation(false);
          }
        } else {
          //console.log('Device has no location information');
          setDeviceLocation(null);
        }
      }
    };

    fetchDeviceLocation();
  }, [selectedDevice]);

  // Determine the location to use for the ticket
  const getLocationForTicket = () => {
    // If creating from location page, use that location
    if (locationData && !selectedDevice) {
      //console.log('Using location from locationData:', locationData);
      return locationData;
    }

    // If creating from device page
    if (selectedDevice) {
      // First check if device has embedded location object
      if (selectedDevice.location) {
        //console.log('Using embedded location from device:', selectedDevice.location);
        return selectedDevice.location;
      }

      // Use fetched location data if available
      if (deviceLocation) {
        //console.log('Using fetched location for device:', deviceLocation);
        return deviceLocation;
      }

      // If device only has locationId and we're still fetching, create minimal object
      if (selectedDevice.locationId || selectedDevice.location_id) {
        const locId = selectedDevice.locationId || selectedDevice.location_id;
        //console.log('Creating minimal location from device locationId:', locId);
        return {
          id: locId,
          name: selectedDevice.locationName || `Location ${locId}`,
          friendlyName: selectedDevice.locationName || `Location ${locId}`
        };
      }
    }

    // Fallback to just locationId if available
    if (locationId) {
      //console.log('Using fallback locationId:', locationId);
      return { id: locationId };
    }

    //console.log('No location found');
    return null;
  };

  const createFormDialog = createDialogOpen &&
  <CreateForm
    preSelectedDevice={selectedDevice}
    preSelectedLocation={getLocationForTicket()}
    embedded={true}
    onClose={handleCloseDialog}
    onSuccess={handleTicketCreated}
    fetchingLocation={fetchingLocation} />;



  if (loading) {
    return (
      <Box>
        {createTicketButton}
        {createFormDialog}
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      </Box>);

  }

  if (error) {
    return (
      <Box>
        {createTicketButton}
        {createFormDialog}
        <Box p={2}>
          <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>{t ? t("LogsTable.retry") : "Retry"}

          </Button>
          }>
            {error}
          </Alert>
        </Box>
      </Box>);

  }

  if (!tickets || tickets.length === 0) {
    return (
      <Box>
        {createTicketButton}
        {createFormDialog}
        <Box p={2}>
          <Alert
            severity="info"
            icon={<TicketIcon />}
            action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
                <RefreshIcon sx={{ mr: 1 }} />{t ? t("DeviceStatusDashboard.refresh") : "Refresh"}

            </Button>
            }>{t ? t("TicketsTable.no_tickets_found_this_location") : "No tickets found for this location"}


          </Alert>
        </Box>
      </Box>);

  }

  // Mobile Card View
  const renderMobileView = () =>
  <Box>
      {filteredTickets.map((ticket) =>
    <Card
      key={ticket.id}
      sx={{
        mb: 2,
        cursor: onViewTicket ? 'pointer' : 'default'
      }}
      onClick={() => onViewTicket && onViewTicket(ticket)}>
      
          <CardContent>
            <Stack spacing={2}>
              {/* Header with Ticket Number */}
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {ticket.ticketNumber || `#${ticket.id}`}
                </Typography>
                {getStatusChip(ticket.status)}
              </Stack>

              <Divider />

              {/* Title */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t ? t("VisualTreeView.title") : "Title"}

            </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {ticket.title}
                </Typography>
                {ticket.description &&
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {ticket.description}
                  </Typography>
            }
              </Box>

              {/* Priority */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t ? t("TicketsTable.priority") : "Priority"}

            </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {getPriorityChip(ticket.priority)}
                </Box>
              </Box>

              {/* Assignee */}
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t ? t("TicketsTable.assignee") : "Assignee"}

            </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {ticket.assignee || 'Unassigned'}
                </Typography>
                {ticket.reporter &&
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{t ? t("TicketsTable.reported") : "Reported by:"}
              {ticket.reporter}
                  </Typography>
            }
              </Box>

              {/* Dates */}
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">{t ? t("LocationDetails.created") : "Created"}

              </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {formatDateTime(ticket.createdAt, 'MMM dd')}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">{t ? t("TicketsTable.updated") : "Updated"}

              </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {formatDateTime(ticket.updatedAt, 'MMM dd')}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>

          {(onViewTicket || onEditTicket || canManageTickets && onDeleteTicket) &&
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
              {onViewTicket &&
        <Button
          size="small"
          startIcon={<ViewIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onViewTicket(ticket);
          }}>{t ? t("TicketsTable.view") : "View"}


        </Button>
        }
              {onEditTicket && canManageTickets &&
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onEditTicket(ticket);
          }}>{t ? t("DeviceTable.edit") : "Edit"}


        </Button>
        }
              {onDeleteTicket && canManageTickets &&
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(ticket.id);
          }}>{t ? t("DeviceTable.delete") : "Delete"}


        </Button>
        }
            </CardActions>
      }
        </Card>
    )}

      {filteredTickets.length === 0 &&
    <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{t ? t("TicketsTable.no_tickets_found") : "No tickets found"}</Typography>
        </Paper>
    }
    </Box>;


  if (isMobile) {
    return (
      <Box>
        {createTicketButton}
        {createFormDialog}
        {renderMobileView()}
        {/* Results Summary */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>{t ? t("LogsTable.showing") : "Showing"}
            {filteredTickets.length}{t ? t("LogsTable.text_6") : "of"}{tickets.length}{t ? t("TicketsTable.tickets") : "tickets"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Chip
              label={`${tickets.filter((t) => t.status?.toLowerCase() === 'open').length} Open`}
              size="small"
              color="error"
              variant="outlined" />
            
            <Chip
              label={`${tickets.filter((t) => t.status?.toLowerCase() === 'in-progress').length} In Progress`}
              size="small"
              color="info"
              variant="outlined" />
            
            <Chip
              label={`${tickets.filter((t) => t.status?.toLowerCase() === 'resolved').length} Resolved`}
              size="small"
              color="success"
              variant="outlined" />
            
          </Stack>
        </Box>
      </Box>);

  }

  return (
    <Box>
      {createTicketButton}
      {createFormDialog}
      {/* Tickets Table */}
      <TableContainer component={Paper}>
        <Table size="small" aria-label={t ? t("TicketsTable.tickets_table") : "tickets table"}>
          <TableHead>
            <TableRow>
              <TableCell>{t ? t("VisualTreeView.ticket") : "Ticket #"}</TableCell>
              <TableCell>{t ? t("VisualTreeView.title") : "Title"}</TableCell>
              <TableCell>{t ? t("DeviceTable.status") : "Status"}</TableCell>
              <TableCell>{t ? t("TicketsTable.priority") : "Priority"}</TableCell>
              <TableCell>{t ? t("TicketsTable.assignee") : "Assignee"}</TableCell>
              <TableCell>{t ? t("LocationDetails.created") : "Created"}</TableCell>
              <TableCell>{t ? t("TicketsTable.updated") : "Updated"}</TableCell>
              {(onViewTicket || onEditTicket || canManageTickets && onDeleteTicket) &&
              <TableCell align="right">{t ? t("DeviceTable.actions") : "Actions"}</TableCell>
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTickets.map((ticket) =>
            <TableRow
              key={ticket.id}
              hover
              sx={{ cursor: onViewTicket ? 'pointer' : 'default' }}
              onClick={() => onViewTicket && onViewTicket(ticket)}>
              
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {ticket.ticketNumber || `#${ticket.id}`}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {ticket.title}
                    </Typography>
                    {ticket.description &&
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                        {ticket.description}
                      </Typography>
                  }
                  </Box>
                </TableCell>
                <TableCell>{getStatusChip(ticket.status)}</TableCell>
                <TableCell>{getPriorityChip(ticket.priority)}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {ticket.assignee || 'Unassigned'}
                  </Typography>
                  {ticket.reporter &&
                <Typography variant="caption" color="text.secondary">{t ? t("TicketsTable.reported") : "Reported by:"}
                  {ticket.reporter}
                    </Typography>
                }
                </TableCell>
                <TableCell>
                  <Tooltip title={formatDateTime(ticket.createdAt)}>
                    <Typography variant="body2">
                      {formatDateTime(ticket.createdAt, 'MMM dd')}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={formatDateTime(ticket.updatedAt)}>
                    <Typography variant="body2">
                      {formatDateTime(ticket.updatedAt, 'MMM dd')}
                    </Typography>
                  </Tooltip>
                </TableCell>
                {(onViewTicket || onEditTicket || canManageTickets && onDeleteTicket) &&
              <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {onViewTicket &&
                <Tooltip title={t ? t("TicketsTable.view_ticket") : "View Ticket"}>
                        <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewTicket(ticket);
                    }}>
                    
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                }
                    {onEditTicket && canManageTickets &&
                <Tooltip title={t ? t("TicketsTable.edit_ticket") : "Edit Ticket"}>
                        <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditTicket(ticket);
                    }}>
                    
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                }
                    {onDeleteTicket && canManageTickets &&
                <Tooltip title={t ? t("VisualTreeView.delete_ticket") : "Delete Ticket"}>
                        <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(ticket.id);
                    }}>
                    
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                }
                  </TableCell>
              }
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Results Summary */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">{t ? t("LogsTable.showing") : "Showing"}
          {filteredTickets.length}{t ? t("LogsTable.text_6") : "of"}{tickets.length}{t ? t("TicketsTable.tickets") : "tickets"}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label={`${tickets.filter((t) => t.status?.toLowerCase() === 'open').length} Open`}
            size="small"
            color="error"
            variant="outlined" />
          
          <Chip
            label={`${tickets.filter((t) => t.status?.toLowerCase() === 'in-progress').length} In Progress`}
            size="small"
            color="info"
            variant="outlined" />
          
          <Chip
            label={`${tickets.filter((t) => t.status?.toLowerCase() === 'resolved').length} Resolved`}
            size="small"
            color="success"
            variant="outlined" />
          
        </Box>
      </Box>
    </Box>);

};

export default TicketsTable;